import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './AccountingSoftware.module.scss';
import RailzConnect from './RailzConnect/RailzConnectFrame';
import { getRequestedRailzService } from './railzServices';
import useFetchRailzBusinessAccount from '../../../hooks/useApi/useFetchRailzBusinessAccount';
import useFetchRailzAccount from '../../../hooks/useApi/useFetchRailzAccount';
import useFetchRailzChartOfAccounts from '../../../hooks/useApi/useFetchRailzChartOfAccounts';
import usePostSetRailzChartOfAccount from '../../../hooks/useApi/usePostSetRailzChartOfAccount';
import RailzConnectContext, { RailzConnectCtx } from './Dialogue/RailzConnectContext';
import RailzConnectDialogue from './Dialogue/RailzConnectDialogue';
import { RailzConnectDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import ConnectSoftware from './ConnectSoftware/ConnectSoftware';
import ChartOfAccounts from './ChartOfAccounts/ChartOfAccounts';
import { ChartOfAccountsList } from './types';

import { useDataFetchIndicator } from '../../../hooks';

const { Box } = vp;
type AccountingSoftwareProps = {
  redirectURL?: string;
  isSettings?: boolean;
  onCompleteChartOfAccount?: (chartOfAccountNameReceivable: string, chartOfAccountNamePayable: string) => void;
};

type FC = FunctionComponent<AccountingSoftwareProps>;

const AccountingSoftware: FC = ({
  redirectURL,
  onCompleteChartOfAccount,
  isSettings = false,
}: AccountingSoftwareProps) => {
  const history = useHistory();
  // An ERP card links here with ?service=<railz service>; the connect widget is then scoped to it
  const requestedService = getRequestedRailzService(useLocation().search);
  const [isRailzConnect, setIsRailzConnect] = useState(false);
  const [railzBusinessAccountResponse, fetchRailzBusinessAccount] = useFetchRailzBusinessAccount({});
  const [, , fetchRailzOfAccountsAsync] = useFetchRailzAccount({});
  const [, , fetchChartOfAccountsAsync] = useFetchRailzChartOfAccounts({});
  const [, , postSetChartOfAccountAsync] = usePostSetRailzChartOfAccount({});
  const [currentChartReceivable, setCurrentChartReceivables] = useState('');
  const [currentChartPayable, setCurrentChartPayable] = useState('');
  const [originalChartReceivable, setOriginalChartReceivables] = useState('');
  const [originalChartPayables, setOriginalChartPayables] = useState('');
  const [chartsReceivables, setChartsReceivables] = useState<ChartOfAccountsList>({});
  const [chartsPayables, setChartsPayables] = useState<ChartOfAccountsList>({});

  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const [showConnect, setShowConnect] = useState(true);

  const [showUpdateSuccessAlert, setShowUpdateSuccessAlert] = useState(false);
  const [showUpdateErrorAlert, setShowUpdateErrorAlert] = useState(false);
  const [receivableName, setReceivableName] = useState('');
  const [payableName, setPayableName] = useState('');
  const [error, setError] = useState('');
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const railzConnectCtx: RailzConnectCtx = {
    showUpdateSuccessAlert,
    showUpdateErrorAlert,
    showConfirmUpdate,
    receivableName,
    payableName,
    error,
  };

  const saveSelection = async () => {
    const selectedPayable = Object.values(chartsPayables).find((chart) => chart.id === currentChartPayable);
    const selectedReceivable = Object.values(chartsReceivables).find((chart) => chart.id === currentChartReceivable);

    if (!selectedPayable || !selectedReceivable) return;
    setDataFetchingInfo(true, 'Loading...', 'fullScreen');
    setPayableName(selectedPayable.name);
    setReceivableName(selectedReceivable.name);

    try {
      const [setChartOfAccountsReceivables, setChartOfAccountsPayables] = await Promise.all([
        postSetChartOfAccountAsync({
          BusinessName: railzBusinessAccountResponse?.Accounts[0].BusinessName || '',
          ServiceName: railzBusinessAccountResponse?.Accounts[0].ServiceName || '',
          Account: selectedReceivable,
          Type: 'receivables',
        }),
        postSetChartOfAccountAsync({
          BusinessName: railzBusinessAccountResponse?.Accounts[0].BusinessName || '',
          ServiceName: railzBusinessAccountResponse?.Accounts[0].ServiceName || '',
          Account: selectedPayable,
          Type: 'payables',
        }),
      ]);

      if (setChartOfAccountsPayables.Success && setChartOfAccountsReceivables.Success && onCompleteChartOfAccount) {
        onCompleteChartOfAccount(selectedReceivable?.name || '', selectedPayable?.name || '');
      } else if (!setChartOfAccountsPayables.Success) {
        setError(setChartOfAccountsPayables.ErrorMessage || '');
        setShowUpdateErrorAlert(true);
      } else if (!setChartOfAccountsReceivables.Success) {
        setError(setChartOfAccountsReceivables.ErrorMessage || '');
        setShowUpdateErrorAlert(true);
      }

      if (isSettings) {
        setShowUpdateSuccessAlert(true);
      }
    } catch {
      setError('Something unexpected happened. Please try again later.');
      setShowUpdateErrorAlert(true);
    } finally {
      setDataFetchingInfo(false, 'Loading...', 'fullScreen');
    }
  };

  const handleDialogue = ({ type, state, action = 'default' }: DialogueHandlerProps<RailzConnectDialogueHandler>) => {
    const dialogueHandlers: RailzConnectDialogueHandler = {
      error: {
        success: {
          default: () => {
            setShowUpdateErrorAlert(false);
            setError('');
          },
        },
        error: {
          default: () => {
            setShowUpdateErrorAlert(false);
            setError('');
          },
        },
      },
      save: {
        success: {
          default: () => {
            setShowUpdateSuccessAlert(false);
            history.push('/payables');
          },
        },
        error: {
          default: () => {
            history.push('/integrations');
            setShowUpdateSuccessAlert(false);
          },
        },
      },
      confirm: {
        success: {
          default: () => {
            setShowConfirmUpdate(false);
            void saveSelection();
          },
        },
        error: {
          default: () => {
            setShowConfirmUpdate(false);
          },
        },
      },
    };

    const dialogueState = dialogueHandlers[type][state];
    const handler = dialogueState[action] || dialogueState.default;

    handler();
  };

  useEffect(() => {
    fetchRailzBusinessAccount({});
  }, []);

  useEffect(() => {
    if (railzBusinessAccountResponse && railzBusinessAccountResponse.Success) {
      const calculateShowConnect = Object.values(railzBusinessAccountResponse.Accounts).length === 0;
      setShowConnect(calculateShowConnect);
      // Coming from a specific ERP card: skip the intro and open the widget straight away
      if (calculateShowConnect && requestedService) setIsRailzConnect(true);

      if (!calculateShowConnect) {
        const getInformationForChartOfAccounts = async () => {
          setDataFetchingInfo(false, 'Loading...', 'fullScreen');
          setDataFetchingInfo(true, 'Loading...', 'fullScreen');

          const [resReceivablesAccount, resPayablesAccount, resReceivableChartOfAccounts, resPayablesChartOfAccounts] =
            await Promise.all([
              fetchRailzOfAccountsAsync({
                BusinessName: railzBusinessAccountResponse.Accounts[0].BusinessName,
                ServiceName: railzBusinessAccountResponse.Accounts[0].ServiceName,
                Type: 'receivables',
              }),
              fetchRailzOfAccountsAsync({
                BusinessName: railzBusinessAccountResponse.Accounts[0].BusinessName,
                ServiceName: railzBusinessAccountResponse.Accounts[0].ServiceName,
                Type: 'payables',
              }),
              fetchChartOfAccountsAsync({
                BusinessName: railzBusinessAccountResponse.Accounts[0].BusinessName,
                ServiceName: railzBusinessAccountResponse.Accounts[0].ServiceName,
                Type: 'receivables',
              }),
              fetchChartOfAccountsAsync({
                BusinessName: railzBusinessAccountResponse.Accounts[0].BusinessName,
                ServiceName: railzBusinessAccountResponse.Accounts[0].ServiceName,
                Type: 'payables',
              }),
            ]);

          if (resReceivablesAccount.Success) {
            setCurrentChartReceivables(resReceivablesAccount?.Account?.AccountRefID || '');
            setOriginalChartReceivables(resReceivablesAccount?.Account?.AccountRefID || '');
          }

          if (resPayablesAccount.Success) {
            setCurrentChartPayable(resPayablesAccount?.Account?.AccountRefID || '');
            setOriginalChartPayables(resPayablesAccount?.Account?.AccountRefID || '');
          }

          if (resReceivableChartOfAccounts.Success) {
            setChartsReceivables(resReceivableChartOfAccounts.Accounts || {});
          }

          if (resPayablesChartOfAccounts.Success) {
            setChartsPayables(resPayablesChartOfAccounts.Accounts || {});
          }

          setDataFetchingInfo(false, 'Loading...', 'fullScreen');
          setLoaded(true);
        };

        getInformationForChartOfAccounts();
      } else {
        setLoaded(true);
      }
    }
  }, [railzBusinessAccountResponse]);

  const { protocol, port, hostname } = window.location;
  const isLocalHost = hostname === 'localhost' || hostname === 'bs-local.com';
  const origin = isLocalHost ? `${protocol}//${hostname}:${port || '3000'}` : `${protocol}//${hostname}`;
  const pageURL = `${origin}/integrations/accounting-software/`;

  // Keep the ERP scope when Railz sends the user back here after connecting
  const RedirectURL = requestedService ? `${pageURL}?service=${requestedService}` : pageURL;
  // ...but never on close: returning with ?service= re-opens the widget immediately, so there is
  // no way out of the connect flow. Closing a scoped widget goes back to the marketplace instead.
  const CloseRedirectURL = requestedService ? `${origin}/integrations` : pageURL;

  return (
    <RailzConnectContext.Provider value={railzConnectCtx}>
      {loaded && (
        <Box flx="row" jc="sa">
          <div className={styles.container}>
            <Box flx="col">
              {!showConnect && !isRailzConnect && (
                <ChartOfAccounts
                  onSave={() => {
                    if (originalChartPayables === '' || originalChartReceivable === '') {
                      saveSelection();
                    } else {
                      setShowConfirmUpdate(true);
                    }
                  }}
                  currentChartReceivable={currentChartReceivable}
                  currentChartPayable={currentChartPayable}
                  originalChartReceivable={originalChartReceivable}
                  originalChartPayables={originalChartPayables}
                  chartsReceivables={chartsReceivables}
                  chartsPayables={chartsPayables}
                  onReceivableChange={setCurrentChartReceivables}
                  onPayableChange={setCurrentChartPayable}
                  serviceName={railzBusinessAccountResponse?.Accounts[0].ServiceName}
                />
              )}

              {showConnect && !isRailzConnect && (
                <ConnectSoftware
                  onConnect={() => {
                    setIsRailzConnect(true);
                  }}
                />
              )}

              {isRailzConnect && (
                <RailzConnect
                  redirectURL={redirectURL || RedirectURL}
                  closeRedirectURL={redirectURL || CloseRedirectURL}
                  service={requestedService}
                />
              )}
            </Box>
          </div>
        </Box>
      )}

      <RailzConnectDialogue handleDialogue={handleDialogue} />
    </RailzConnectContext.Provider>
  );
};

export default AccountingSoftware;
